/* eslint-disable react-hooks/refs -- lazy-load drafts on first render
 * so the consumer's channelId-change effect can read the map
 * synchronously. Same render-phase ref pattern that `use-message-window`
 * uses for its committed-slice tracking. */
import { useCallback, useEffect, useMemo, useRef } from "react";

const STORAGE_KEY_PREFIX = "ano-drafts:";
const MAX_DRAFTS = 100;
const WRITE_DEBOUNCE_MS = 300;

interface DraftEntry {
  md: string;
  updatedAt: number;
}

type DraftMap = Record<string, DraftEntry>;

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

export function loadDraftsFromStorage(userId: string | null): DraftMap {
  if (!userId || typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out: DraftMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (
        v &&
        typeof v === "object" &&
        typeof (v as DraftEntry).md === "string" &&
        typeof (v as DraftEntry).updatedAt === "number"
      ) {
        out[k] = v as DraftEntry;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function saveDraftsToStorage(userId: string | null, drafts: DraftMap): void {
  if (!userId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(drafts));
  } catch {
    // Quota exceeded / private mode — silent.
  }
}

export function pruneDraftsToMax(drafts: DraftMap, max: number): DraftMap {
  const entries = Object.entries(drafts);
  if (entries.length <= max) return drafts;
  entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt);
  return Object.fromEntries(entries.slice(0, max));
}

export interface ChannelDraftsApi {
  /** Returns the saved markdown for `channelId`, or `null`. */
  get(channelId: string): string | null;
  /**
   * Stores `md` for `channelId`. Whitespace-only content is treated as
   * "no draft" and removed from the map.
   */
  set(channelId: string, md: string): void;
  /** Removes the draft for `channelId` (call after a successful send). */
  clear(channelId: string): void;
}

/**
 * Per-channel composer drafts persisted in localStorage, keyed by the
 * current user's id. Drafts survive reloads / Electron restarts.
 *
 * Storage key: `ano-drafts:<userId>`. Body: `Record<channelId, { md,
 * updatedAt }>`. Capped to `MAX_DRAFTS` newest entries — pruning runs
 * only at hook-init / userId-change (load path). A single session
 * that creates >MAX_DRAFTS distinct drafts without reload could grow
 * the in-memory map past the cap; it self-corrects on the next load.
 * Not worth a per-`set` prune for the rate at which real users
 * accumulate drafts.
 *
 * Writes are debounced (`WRITE_DEBOUNCE_MS`) and best-effort flushed
 * on `visibilitychange:hidden` / `beforeunload` / hook unmount so a
 * sudden close doesn't drop the in-flight keystrokes.
 *
 * Cap=1 assumption (current `MULTIPLEX_CAP`): the consumer's
 * `MessageInput` fiber persists across channel switches, so a
 * `useRef`-backed in-memory map inside the hook is the single source
 * of truth that all switches feed through. If the cap is ever bumped
 * the hook needs to lift to a context or App-level singleton.
 */
export function useChannelDrafts(userId: string | null): ChannelDraftsApi {
  // Lazy-load on first render and re-load on userId change. Reading +
  // mutating refs during render is safe for this kind of derivation
  // (deterministic, depends only on declared inputs).
  const draftsRef = useRef<DraftMap>({});
  const loadedUserIdRef = useRef<string | null | undefined>(undefined);
  if (loadedUserIdRef.current !== userId) {
    draftsRef.current = pruneDraftsToMax(
      loadDraftsFromStorage(userId),
      MAX_DRAFTS,
    );
    loadedUserIdRef.current = userId;
  }

  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushNow = useCallback(() => {
    if (flushTimerRef.current != null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    saveDraftsToStorage(loadedUserIdRef.current ?? null, draftsRef.current);
  }, []);

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current != null) clearTimeout(flushTimerRef.current);
    flushTimerRef.current = setTimeout(flushNow, WRITE_DEBOUNCE_MS);
  }, [flushNow]);

  useEffect(() => {
    const onVisibility = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        flushNow();
      }
    };
    const onBeforeUnload = () => flushNow();
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", onBeforeUnload);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("beforeunload", onBeforeUnload);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      flushNow();
    };
  }, [flushNow]);

  return useMemo<ChannelDraftsApi>(
    () => ({
      get: (channelId: string): string | null => {
        return draftsRef.current[channelId]?.md ?? null;
      },
      set: (channelId: string, md: string): void => {
        if (md.trim().length === 0) {
          if (channelId in draftsRef.current) {
            delete draftsRef.current[channelId];
            scheduleFlush();
          }
          return;
        }
        draftsRef.current[channelId] = { md, updatedAt: Date.now() };
        scheduleFlush();
      },
      clear: (channelId: string): void => {
        if (channelId in draftsRef.current) {
          delete draftsRef.current[channelId];
          scheduleFlush();
        }
      },
    }),
    [scheduleFlush],
  );
}
